import { useCallback, useEffect, useRef } from 'react';
import { useWorkspace } from '@/contexts/workspace-context';
import { useError } from '@/contexts/error-context';
import { useSession } from '@/contexts/session-context';
import { captureFrontendError } from '@/lib/sentry';
import { peekHandoffJwt } from '@/lib/handoff-storage';
import { withAuthRetry } from '@/lib/with-auth-retry';

const API_URL = import.meta.env.VITE_API_URL ?? '';

export function useFetch() {
  const { activeWorkspace } = useWorkspace();
  const { showErrorPage } = useError();
  const { signIn } = useSession();

  // Keep showErrorPage in a ref for stable fetchWithAuth identity
  const showErrorPageRef = useRef(showErrorPage);
  useEffect(() => {
    showErrorPageRef.current = showErrorPage;
  }, [showErrorPage]);

  const fetchWithAuth = useCallback(
    async (
      path: string,
      options?: RequestInit,
      // Non-critical reads (e.g. a sidebar visibility probe) opt out of the
      // global full-screen ErrorPage escalation so their own fail-soft handling
      // holds — one widget's 5xx/network blip must not nuke the whole app.
      meta?: { suppressGlobalError?: boolean },
    ) => {
      const method = options?.method?.toUpperCase() ?? 'GET';

      const execute = async (): Promise<Response> => {
        // Phase 129 Plan 09 / Phase 130 — peek the demo→signup handoff JWT
        // from sessionStorage and attach it as `X-Handoff-JWT`.
        const handoffJwt = peekHandoffJwt();

        // Headers class merges case-insensitively — a plain-object spread would
        // duplicate the key when a caller passes lowercase `content-type`, and
        // fetch comma-joins duplicates, breaking NestJS's body-parser.
        const headers = new Headers(options?.headers);
        // A JSON.stringify'd body without this header parses as text/plain
        // server-side and Nest sees an empty body (AIT-174). A caller's own
        // content type wins.
        if (typeof options?.body === 'string' && !headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
        // Phase 117: activeWorkspace.id is a branded `WorkspacePublicId`
        // (`ws_<8 alphanum>`). Backend WorkspaceGuard 400s any other shape.
        // Injection is a DEFAULT, never an override: a caller that sets its
        // own X-Workspace-Id (connect flows targeting a picked customer) must
        // win. Unconditional set() here silently clobbered the picked target
        // with the cached activeWorkspace and connected channels to the wrong
        // workspace (AIT-207).
        if (activeWorkspace && !headers.has('X-Workspace-Id')) {
          headers.set('X-Workspace-Id', activeWorkspace.id);
        }
        if (handoffJwt) headers.set('X-Handoff-JWT', handoffJwt);

        return fetch(`${API_URL}${path}`, {
          ...options,
          credentials: 'include',
          headers,
        });
      };

      try {
        const response = await withAuthRetry({
          execute,
          // Only auto-route through signIn for GET reads. For mutating writes
          // (POST/PUT/PATCH/DELETE) the caller surfaces the 401 itself
          // (typically as an error toast) so the user isn't yanked away
          // mid-action with their pending input lost. The unit cover of this
          // distinction is in use-fetch.test.ts.
          onUnauthorized: method === 'GET' ? () => signIn() : () => {},
        });

        if (response.status >= 500 && method === 'GET' && !meta?.suppressGlobalError) {
          showErrorPageRef.current({
            message: 'Server error',
            url: path,
            status: response.status,
          });
        }

        return response;
      } catch (error) {
        if (error instanceof TypeError && method === 'GET' && !meta?.suppressGlobalError) {
          // fetch TypeError = the request never got a response (network blip,
          // navigation-aborted fetch). Sev2, not sev1 — see AIT-335.
          captureFrontendError(error, { url: path });
          showErrorPageRef.current({
            message: 'Network error',
            url: path,
          });
        }
        throw error;
      }
    },
    [activeWorkspace, signIn],
  );

  return fetchWithAuth;
}
